// src/pages/AssessmentTake.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assessmentsApi } from '../services/api';
import PrivateNotesField from '../components/PrivateNotesField';
import VoiceInput from '../components/VoiceInput';

const AssessmentTake = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [sections, setSections] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    loadAssessment();
  }, [assessmentId]);

  const loadAssessment = async () => {
    try {
      const [assessmentResponse, responsesResponse] = await Promise.all([
        assessmentsApi.getById(assessmentId),
        assessmentsApi.getResponses(assessmentId)
      ]);

      setAssessment(assessmentResponse.data);
      
      // Process sections - group if 3-level hierarchy detected
      const processedSections = processSections(responsesResponse.data.sections || []);
      setSections(processedSections);

      // Build responses map - now includes private_notes and question_id
      const responsesMap = {};
      responsesResponse.data.sections?.forEach(section => {
        section.questions?.forEach(question => {
          if (question.response_id) {
            responsesMap[question.response_id] = {
              questionId: question.id,
              answer: question.answer,
              notes: question.notes || '',
              private_notes: question.private_notes || '' // Add private notes to response map
            };
          } else {
            // For questions without responses yet (e.g., from migration), store by question ID temporarily
            responsesMap[`q_${question.id}`] = {
              questionId: question.id,
              answer: null,
              notes: '',
              private_notes: ''
            };
          }
        });
      });
      setResponses(responsesMap);

      // Expand first section by default
      if (processedSections.length > 0) {
        setExpandedSections({ [processedSections[0].id]: true });
      }
    } catch (error) {
      setError('Failed to load assessment');
      console.error('Error loading assessment:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Process sections to handle different hierarchy levels
   * - 2-level (NIST): Return as-is
   * - 3-level (CMMC): Group by subcategory (parent)
   */
  const processSections = (rawSections) => {
    if (!rawSections || rawSections.length === 0) return [];

    // Detect if this is a 3-level hierarchy by checking for pattern like "AC.L1-3.1.1[a]"
    // Sub-controls have brackets like [a], [b], [c]
    const hasSubControls = rawSections.some(section => 
      section.code && /\[.+\]/.test(section.code)
    );

    if (!hasSubControls) {
      // 2-level hierarchy (NIST) - return as-is
      return rawSections;
    }

    // 3-level hierarchy (CMMC) - group by subcategory
    // Extract parent name by removing the bracketed part
    const grouped = {};
    
    rawSections.forEach(section => {
      // Extract parent name (e.g., "Authorized Access Control (AC.L1-3.1.1[a])" -> "Authorized Access Control (AC.L1-3.1.1)")
      let parentName = section.name;
      let parentCode = section.code;
      
      // Remove bracketed part from both name and code
      parentName = parentName.replace(/\s*\[.+?\]\s*$/, '');
      parentCode = parentCode ? parentCode.replace(/\[.+?\]$/, '') : '';
      
      // Create or update grouped section
      if (!grouped[parentCode]) {
        grouped[parentCode] = {
          id: `grouped-${parentCode}`,
          name: parentName,
          code: parentCode,
          description: section.description,
          questions: [],
          subSections: []
        };
      }
      
      // Add this sub-control's questions to the parent
      if (section.questions) {
        grouped[parentCode].questions.push(...section.questions);
      }
      
      // Store sub-section info for reference
      grouped[parentCode].subSections.push({
        name: section.name,
        code: section.code
      });
    });

    return Object.values(grouped);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleAnswerChange = async (responseIdOrKey, answer, questionId = null) => {
    const key = responseIdOrKey;
    const responseData = responses[key];
    const actualQuestionId = questionId || responseData?.questionId;
    
    // Update local state immediately for UI responsiveness
    const newResponses = {
      ...responses,
      [key]: {
        ...responseData,
        answer,
        questionId: actualQuestionId
      }
    };
    setResponses(newResponses);

    // If this is a temporary key (starts with 'q_'), we need to create the response first
    if (typeof key === 'string' && key.startsWith('q_')) {
      try {
        setSaving(true);
        // Create new response
        const response = await assessmentsApi.createResponse(assessmentId, {
          question_id: actualQuestionId,
          answer: answer
        });
        
        const newResponseId = response.data.response_id;
        
        // Update responses state with the new response_id, remove temporary key
        setResponses(prev => {
          const updated = { ...prev };
          delete updated[key]; // Remove the temporary q_ key
          updated[newResponseId] = {
            ...prev[key],
            questionId: actualQuestionId,
            answer: answer
          };
          return updated;
        });
        
        // IMPORTANT: Update the sections state to give the question its response_id
        setSections(prevSections => {
          return prevSections.map(section => ({
            ...section,
            questions: section.questions?.map(q => 
              q.id === parseInt(actualQuestionId) 
                ? { ...q, response_id: newResponseId }
                : q
            )
          }));
        });
        
        setSaveMessage('Saved');
        setTimeout(() => setSaveMessage(''), 2000);
      } catch (error) {
        console.error('Error creating response:', error);
        setSaveMessage('Error saving');
      } finally {
        setSaving(false);
      }
    } else {
      // Normal update path - response already exists
      await saveResponse(key, newResponses[key]);
    }
  };

  const handleNotesChange = (responseId, notes) => {
    setResponses(prev => ({
      ...prev,
      [responseId]: {
        ...prev[responseId],
        notes
      }
    }));
  };

  const saveResponse = async (responseId, data) => {
    try {
      setSaving(true);
      await assessmentsApi.updateResponse(assessmentId, responseId, data);
      setSaveMessage('Saved');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (error) {
      console.error('Error saving response:', error);
      setSaveMessage('Error saving');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async (responseKey) => {
    // Only save if this is a real response_id (not a temporary q_ key)
    if (typeof responseKey === 'string' && responseKey.startsWith('q_')) {
      setSaveMessage('Please answer the question first');
      setTimeout(() => setSaveMessage(''), 2000);
      return;
    }
    await saveResponse(responseKey, responses[responseKey]);
  };

  // Handler for when private notes are saved via PrivateNotesField component
  const handlePrivateNotesSaved = (responseId, privateNotes) => {
    setResponses(prev => ({
      ...prev,
      [responseId]: {
        ...prev[responseId],
        private_notes: privateNotes
      }
    }));
  };

  const handleSubmit = async () => {
    if (!window.confirm('Are you sure you want to submit this assessment? You cannot modify it after submission unless it is reopened.')) {
      return;
    }

    try {
      setSaving(true);
      await assessmentsApi.submit(assessmentId);
      alert('Assessment submitted successfully!');
      navigate(`/assessments/${assessmentId}/results`);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to submit assessment';
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  const calculateProgress = () => {
    let total = 0;
    let answered = 0;

    sections.forEach(section => {
      section.questions?.forEach(question => {
        total++;
        const responseKey = question.response_id || `q_${question.id}`;
        if (responses[responseKey]?.answer) {
          answered++;
        }
      });
    });

    return { total, answered, percentage: total > 0 ? Math.round((answered / total) * 100) : 0 };
  };

  const getSectionProgress = (section) => {
    let total = section.questions?.length || 0;
    let answered = 0;

    section.questions?.forEach(question => {
      const responseKey = question.response_id || `q_${question.id}`;
      if (responses[responseKey]?.answer) {
        answered++;
      }
    });

    return { total, answered };
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error || !assessment) {
    return (
      <div>
        <h1>Error</h1>
        <p>{error || 'Assessment not found'}</p>
        <button onClick={() => navigate('/assessments')}>Back to Assessments</button>
      </div>
    );
  }

  const progress = calculateProgress();

  return (
    <div>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        marginBottom: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0, marginBottom: '5px' }}>{assessment.name}</h1>
            <p style={{ margin: 0, color: '#666' }}>{assessment.client_name} - {assessment.assessment_type_name}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: progress.percentage === 100 ? '#27ae60' : '#333'
            }}>
              {progress.percentage}%
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              {progress.answered} of {progress.total} answered
            </div>
            {saveMessage && (
              <div style={{
                marginTop: '10px',
                padding: '5px 10px',
                backgroundColor: saveMessage.includes('Error') ? '#fee' : '#efe',
                color: saveMessage.includes('Error') ? '#c00' : '#060',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                {saveMessage}
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{
          width: '100%',
          height: '8px',
          backgroundColor: '#e0e0e0',
          borderRadius: '4px',
          marginTop: '15px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${progress.percentage}%`,
            height: '100%',
            backgroundColor: progress.percentage === 100 ? '#27ae60' : '#3498db',
            transition: 'width 0.3s ease'
          }} />
        </div>

        {/* Submit Button */}
        <div style={{ marginTop: '15px' }}>
          <button
            onClick={handleSubmit}
            disabled={progress.answered < progress.total || saving}
            style={{
              padding: '10px 20px',
              backgroundColor: progress.answered >= progress.total ? '#27ae60' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: progress.answered >= progress.total ? 'pointer' : 'not-allowed',
              fontWeight: 'bold'
            }}
          >
            {saving ? 'Submitting...' : 'Submit Assessment'}
          </button>
        </div>
      </div>

      {/* Sections */}
      <div>
        {sections.map(section => {
          const sectionProgress = getSectionProgress(section);
          const isExpanded = expandedSections[section.id];

          return (
            <div
              key={section.id}
              style={{
                backgroundColor: 'white',
                marginBottom: '10px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}
            >
              {/* Section Header */}
              <div
                onClick={() => toggleSection(section.id)}
                style={{
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: isExpanded ? '1px solid #dee2e6' : 'none'
                }}
              >
                <div>
                  <h2 style={{ margin: 0, marginBottom: '5px' }}>{section.name}</h2>
                  {section.code && (
                    <p style={{ margin: 0, fontSize: '14px', color: '#666', fontFamily: 'monospace' }}>
                      {section.code}
                    </p>
                  )}
                  {section.subSections && section.subSections.length > 0 && (
                    <p style={{ margin: 0, marginTop: '5px', fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
                      {section.subSections.length} control requirements
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 'bold', color: '#27ae60' }}>
                      {sectionProgress.answered} / {sectionProgress.total}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>completed</div>
                  </div>
                  <div style={{
                    fontSize: '24px',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}>
                    ▼
                  </div>
                </div>
              </div>

              {/* Questions */}
              {isExpanded && (
                <div style={{ padding: '20px' }}>
                  {section.questions?.map((question, index) => {
                    // Use response_id if it exists, otherwise use temporary q_ key
                    const responseKey = question.response_id || `q_${question.id}`;
                    const response = responses[responseKey] || {};
                    const isAnswered = !!response.answer;

                    return (
                      <div
                        key={question.id}
                        style={{
                          padding: '20px',
                          marginBottom: '15px',
                          border: '1px solid #dee2e6',
                          borderRadius: '8px',
                          backgroundColor: isAnswered ? '#f0f8f0' : 'white'
                        }}
                      >
                        {/* Question Text */}
                        <div style={{ marginBottom: '15px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <h4 style={{ margin: 0 }}>
                              {index + 1}. {question.question_text}
                            </h4>
                            {question.subcategory_code && (
                              <span style={{
                                fontSize: '12px',
                                color: '#666',
                                backgroundColor: '#e9ecef',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontFamily: 'monospace'
                              }}>
                                {question.subcategory_code}
                              </span>
                            )}
                          </div>
                          {question.guidance && (
                            <p style={{
                              margin: 0,
                              fontSize: '14px',
                              color: '#666',
                              fontStyle: 'italic',
                              whiteSpace: 'pre-wrap'
                            }}>
                              {question.guidance}
                            </p>
                          )}
                        </div>

                        {/* Answer Options */}
                        <div style={{ marginBottom: '15px' }}>
                          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '10px' }}>
                            Answer:
                          </label>
                          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {[
                              { value: 'met', label: 'Met', color: '#27ae60' },
                              { value: 'partially_met', label: 'Partially Met', color: '#f39c12' },
                              { value: 'not_met', label: 'Not Met', color: '#e74c3c' },
                              { value: 'na', label: 'N/A', color: '#95a5a6' }
                            ].map(option => (
                              <button
                                key={option.value}
                                onClick={() => handleAnswerChange(responseKey, option.value, question.id)}
                                style={{
                                  padding: '10px 20px',
                                  backgroundColor: response.answer === option.value ? option.color : 'white',
                                  color: response.answer === option.value ? 'white' : '#333',
                                  border: `2px solid ${option.color}`,
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontWeight: 'bold',
                                  fontSize: '14px'
                                }}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Notes */}
                        <div>
                          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                            Notes (optional):
                            <VoiceInput
                              onTranscript={(text) => handleNotesChange(responseKey, text)}
                              currentValue={response.notes || ''}
                              append={true}
                              disabled={!question.response_id}
                            />
                          </label>
                          <textarea
                            value={response.notes || ''}
                            onChange={(e) => handleNotesChange(responseKey, e.target.value)}
                            onBlur={() => handleSaveNotes(responseKey)}
                            placeholder="Add any notes or comments..."
                            rows="3"
                            disabled={!question.response_id}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #dee2e6',
                              borderRadius: '4px',
                              fontSize: '14px',
                              boxSizing: 'border-box',
                              fontFamily: 'inherit',
                              backgroundColor: question.response_id ? 'white' : '#f5f5f5',
                              cursor: question.response_id ? 'text' : 'not-allowed'
                            }}
                          />
                          {!question.response_id && (
                            <div style={{ fontSize: '12px', color: '#999', marginTop: '5px', fontStyle: 'italic' }}>
                              Please answer the question first before adding notes
                            </div>
                          )}
                        </div>

                        {/* Private Notes Field - Only show if response exists */}
                        {question.response_id && (
                          <PrivateNotesField
                            assessmentId={parseInt(assessmentId)}
                            responseId={question.response_id}
                            initialNotes={response.private_notes || ''}
                            onSave={(notes) => handlePrivateNotesSaved(question.response_id, notes)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AssessmentTake;
